import { ref, Ref } from 'vue';
import TranslationService from 'services/TranslationService';
import { httpClient } from 'utils/http';

export type TranslationStatus = 'complete' | 'partial' | 'none';

export type TranslationFieldType = 'text' | 'textarea' | 'lines';

export interface TranslationFieldSchema {
	key: string;
	label: string;
	type: TranslationFieldType;
	placeholder?: string;
	rows?: number;
	hint?: string;
}

export type TranslationEntry = Record<string, string>;

export type TranslationLanguageId = string | number;

export type TranslationsMap = Record<number, Record<string, TranslationEntry>>;

export interface TranslationLanguage {
	id: TranslationLanguageId;
	name: string;
	default?: boolean;
}

export interface InlineTranslationNotify {
	success: (message: string) => void;
	error: (message: string) => void;
}

export interface UseInlineTranslationsOptions {
	namespace: string;
	fields: TranslationFieldSchema[];
	languages: Ref<TranslationLanguage[]>;
	getLanguageStatus: (entry: TranslationEntry) => TranslationStatus;
	notify: InlineTranslationNotify;
}

const deepClone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

export function useInlineTranslations(options: UseInlineTranslationsOptions) {
	const { namespace, fields, languages, getLanguageStatus, notify } = options;

	const translations: Ref<TranslationsMap> = ref({});
	const originalTranslations: Ref<TranslationsMap> = ref({});
	const expandedRowIds: Ref<Set<number>> = ref(new Set());
	const selectedLanguageId: Ref<TranslationLanguageId | null> = ref(null);
	const isLoadingTranslations: Ref<boolean> = ref(false);
	const isSavingTranslations: Ref<boolean> = ref(false);

	const buildLangIds = (): string => languages.value.map(l => l.id).join(' ');

	const emptyEntry = (): TranslationEntry => {
		const entry: TranslationEntry = {};
		fields.forEach((field) => {
			entry[field.key] = '';
		});
		return entry;
	};

	const parseEntry = (raw: Record<string, unknown> | undefined | null): TranslationEntry => {
		const entry: TranslationEntry = {};
		fields.forEach((field) => {
			const value = raw?.[field.key];
			if (field.type === 'lines') {
				entry[field.key] = Array.isArray(value) ? value.map(l => String(l ?? '')).join('\n') : '';
			} else {
				entry[field.key] = (value as string) || '';
			}
		});
		return entry;
	};

	const buildBundle = (entry: TranslationEntry): Record<string, string | string[]> => {
		const bundle: Record<string, string | string[]> = {};
		fields.forEach((field) => {
			if (field.type === 'lines') {
				bundle[field.key] = (entry[field.key] || '').split('\n').filter(l => l.trim() !== '');
			} else {
				bundle[field.key] = entry[field.key] || '';
			}
		});
		return bundle;
	};

	const fetchTranslations = async (): Promise<Record<string, unknown>> => {
		const params = new URLSearchParams({
			ns: namespace,
			lng: buildLangIds(),
			limit: '0',
		});
		return TranslationService.getMultiTranslation(params) as unknown as Record<string, unknown>;
	};

	const loadAll = async (items: { id: number }[]): Promise<void> => {
		if (languages.value.length === 0 || items.length === 0) return;

		try {
			const data = await fetchTranslations();
			const next: TranslationsMap = {};
			items.forEach((item) => {
				next[item.id] = {};
				languages.value.forEach((lang) => {
					const raw = (data[lang.id] as any)?.[namespace]?.[item.id];
					next[item.id][lang.id] = parseEntry(raw);
				});
			});
			translations.value = next;
			originalTranslations.value = deepClone(next);
		} catch (error) {
			console.error('Failed to load translations:', error);
		}
	};

	const loadSingle = async (id: number): Promise<void> => {
		if (languages.value.length === 0) return;

		isLoadingTranslations.value = true;
		try {
			const data = await fetchTranslations();
			if (!translations.value[id]) {
				translations.value[id] = {};
			}
			languages.value.forEach((lang) => {
				const raw = (data[lang.id] as any)?.[namespace]?.[id];
				translations.value[id][lang.id] = parseEntry(raw);
			});
			originalTranslations.value[id] = deepClone(translations.value[id]);
		} catch (error) {
			console.error('Failed to load translations:', error);
			notify.error('Failed to load translations');
		} finally {
			isLoadingTranslations.value = false;
		}
	};

	const toggleExpand = async (id: number): Promise<void> => {
		if (expandedRowIds.value.has(id)) {
			expandedRowIds.value.delete(id);
		} else {
			expandedRowIds.value.add(id);
			if (!translations.value[id] || Object.keys(translations.value[id]).length === 0) {
				await loadSingle(id);
			}
		}
	};

	const getField = (id: number, langId: TranslationLanguageId, key: string): string => {
		return translations.value[id]?.[langId]?.[key] || '';
	};

	const updateField = (id: number, langId: TranslationLanguageId, key: string, value: string): void => {
		if (!translations.value[id]) {
			translations.value[id] = {};
		}
		if (!translations.value[id][langId]) {
			translations.value[id][langId] = emptyEntry();
		}
		translations.value[id][langId][key] = value;
	};

	const hasUnsavedChanges = (id: number): boolean => {
		const current = translations.value[id];
		const original = originalTranslations.value[id];
		if (!current || !original) return false;
		return JSON.stringify(current) !== JSON.stringify(original);
	};

	const getLanguageTranslationStatus = (id: number, langId: TranslationLanguageId): TranslationStatus => {
		const entry = translations.value[id]?.[langId];
		if (!entry) return 'none';
		return getLanguageStatus(entry);
	};

	const getTranslationStatus = (id: number): TranslationStatus => {
		if (languages.value.length === 0) return 'none';

		let completeCount = 0;
		let partialCount = 0;

		languages.value.forEach((lang) => {
			const status = getLanguageTranslationStatus(id, lang.id);
			if (status === 'complete') completeCount++;
			else if (status === 'partial') partialCount++;
		});

		if (completeCount === languages.value.length) return 'complete';
		if (completeCount > 0 || partialCount > 0) return 'partial';
		return 'none';
	};

	const getTranslationStatusClass = (id: number): string => {
		const status = getTranslationStatus(id);
		if (status === 'complete') return 'bg-green-100 text-green-800';
		if (status === 'partial') return 'bg-orange-100 text-orange-800';
		return 'bg-red-100 text-red-800';
	};

	const getTranslationStatusLabel = (id: number): string => {
		const status = getTranslationStatus(id);
		if (status === 'complete') return 'Complete';
		if (status === 'partial') return 'Partial';
		return 'None';
	};

	const getTranslationStatusTitle = (id: number): string => {
		const status = getTranslationStatus(id);
		if (status === 'complete') return 'All translations provided for all languages';
		if (status === 'partial') return 'Some translations are missing';
		return 'No translations provided';
	};

	const saveTranslations = async (id: number): Promise<void> => {
		isSavingTranslations.value = true;
		try {
			for (const lang of languages.value) {
				const entry = translations.value[id]?.[lang.id];
				if (entry) {
					await httpClient.put('/api/translation', {
						language: lang.id,
						namespace,
						bundle: { [id]: buildBundle(entry) },
					});
				}
			}
			originalTranslations.value[id] = deepClone(translations.value[id]);
			notify.success('Translations saved successfully');
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : 'Failed to save translations';
			notify.error(message);
		} finally {
			isSavingTranslations.value = false;
		}
	};

	const cancelTranslationEdit = (id: number): void => {
		if (originalTranslations.value[id]) {
			translations.value[id] = deepClone(originalTranslations.value[id]);
		}
		expandedRowIds.value.delete(id);
	};

	const initSelectedLanguage = (): void => {
		if (languages.value.length > 0 && selectedLanguageId.value === null) {
			const defaultLang = languages.value.find(l => (l as any).default);
			selectedLanguageId.value = defaultLang ? defaultLang.id : languages.value[0].id;
		}
	};

	return {
		fields,
		translations,
		originalTranslations,
		expandedRowIds,
		selectedLanguageId,
		isLoadingTranslations,
		isSavingTranslations,
		loadAll,
		loadSingle,
		toggleExpand,
		getField,
		updateField,
		hasUnsavedChanges,
		getLanguageTranslationStatus,
		getTranslationStatus,
		getTranslationStatusClass,
		getTranslationStatusLabel,
		getTranslationStatusTitle,
		saveTranslations,
		cancelTranslationEdit,
		initSelectedLanguage,
	};
}

export type InlineTranslationController = ReturnType<typeof useInlineTranslations>;
